const HTML_ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * Escapes a value for use as HTML text or inside a quoted attribute.
 * @param {unknown} value
 * @returns {string} Empty string for null or undefined.
 */
export const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => HTML_ENTITIES[char]);

/**
 * Returns the URL unchanged when it resolves to http(s), which covers relative
 * and protocol-relative URLs; otherwise the fallback. Escape the result before
 * putting it in markup.
 * @param {unknown} value
 * @param {string} [fallback]
 * @returns {string}
 */
export const safeUrl = (value, fallback = '') => {
  if (typeof value !== 'string' || value.trim() === '') return fallback;

  try {
    const { protocol } = new URL(value, 'https://base.invalid');
    return protocol === 'http:' || protocol === 'https:' ? value : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Reduces a URL to a path on the current site: the origin is dropped and leading slashes
 * collapse to one, so no input can produce a link to another site. Escape the result
 * before putting it in markup.
 * @param {unknown} value
 * @param {string} [fallback]
 * @returns {string}
 */
export const localPath = (value, fallback = '#') => {
  if (typeof value !== 'string' || value.trim() === '') return fallback;

  try {
    const { protocol, pathname, search, hash } = new URL(
      value,
      'https://base.invalid'
    );
    if (protocol !== 'http:' && protocol !== 'https:') return fallback;
    return `${pathname.replace(/^\/+/, '/')}${search}${hash}`;
  } catch {
    return fallback;
  }
};

/**
 * Returns the value when it can only be a single CSS color (hex, name or a
 * color function), so it cannot add declarations or load a URL; otherwise the
 * fallback.
 * @param {unknown} value
 * @param {string} [fallback]
 * @returns {string}
 */
export const safeCssColor = (value, fallback = '') => {
  const color = String(value ?? '').trim();
  return /^[#\w\s(),.%-]+$/.test(color) ? color : fallback;
};
